import { useState, useEffect } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 16);
      const sections = ['home', 'about', 'leadership', 'activities', 'quality', 'careers', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: t('nav.home'), href: '/#home' },
    { name: t('nav.about'), href: '/#about' },
    { name: t('nav.leadership'), href: '/#leadership' },
    { name: t('nav.activities'), href: '/#activities' },
    { name: t('nav.quality'), href: '/#quality' },
    { name: t('nav.whistleblower'), href: '/whistleBlower' },
    { name: t('nav.careers'), href: '/#careers' },
    { name: t('nav.contact'), href: '/#contact' },
  ];

  const isItemActive = (href: string) => {
    if (href.startsWith('/#')) {
      return location.pathname === '/' && activeSection === href.slice(2);
    }
    return location.pathname.toLowerCase() === href.toLowerCase();
  };

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 interactive-header ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <nav className="container-tight">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" aria-label="Artifex — home">
            <img
              src="/artifex-logo.svg"
              alt="Artifex Systems Slovakia"
              className="h-8 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden xl:flex items-center">
            {navItems.filter((item) => item.href !== '/#contact').map((item) => {
              const isActive = isItemActive(item.href);
              const className = 'nav-link whitespace-nowrap px-2 2xl:px-3';
              if (item.href.startsWith('/#')) {
                return (
                  <a key={item.href} href={item.href} data-active={isActive} className={className}>
                    {item.name}
                  </a>
                );
              }
              return (
                <Link key={item.href} to={item.href} data-active={isActive} className={className}>
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Right cluster */}
          <div className="hidden xl:flex items-center gap-3">
            <ThemeToggle />
            <LanguageSwitcher />
            <div className="w-px h-6 bg-[hsl(var(--hairline))] mx-1" />
            <a
              href="/#contact"
              className="group inline-flex items-center gap-2 font-display text-sm font-semibold text-primary hover:text-secondary transition-colors"
            >
              {t('nav.contact')}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          {/* Mobile cluster */}
          <div className="xl:hidden flex items-center gap-2">
            <ThemeToggle />
            <LanguageSwitcher />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[hsl(var(--hairline))] hover:border-primary transition-colors"
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait" initial={false}>
                {isMenuOpen ? (
                  <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}>
                    <X className="h-5 w-5 text-foreground" />
                  </motion.div>
                ) : (
                  <motion.div key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}>
                    <Menu className="h-5 w-5 text-foreground" />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              className="xl:hidden overflow-hidden"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28 }}
            >
              <div className="pt-4 pb-6 border-t border-[hsl(var(--hairline))] flex flex-col">
                {navItems.map((item, i) => {
                  const isActive = isItemActive(item.href);
                  const className = `group flex w-full items-center justify-between py-4 border-b border-[hsl(var(--hairline))] font-display font-semibold text-lg ${
                    isActive ? 'text-secondary' : 'text-primary hover:text-secondary'
                  } transition-colors`;
                  const content = (
                    <>
                      <span className="flex items-center gap-4">
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        {item.name}
                      </span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </>
                  );
                  if (!item.href.startsWith('/#')) {
                    return (
                      <motion.div
                        key={item.href}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                      >
                        <Link to={item.href} onClick={() => setIsMenuOpen(false)} className={className}>
                          {content}
                        </Link>
                      </motion.div>
                    );
                  }
                  return (
                    <motion.a
                      key={item.href}
                      href={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className={className}
                    >
                      <span className="flex items-center gap-4">
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        {item.name}
                      </span>
                      <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </motion.a>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header;
